import { useEffect, useRef, useCallback } from "react";
import { router } from "@inertiajs/react";
import req from "@/utils/req";
import { UseMonitoramentoNavigationOptions } from "./types";
import { caminhoSemQuery, socketIdDaWeb } from "./identificador";

/**
 * Manda a página nova quando o **caminho** muda numa navegação Inertia.
 *
 * O `page.url` do Inertia traz a query string — os termos de pesquisa e os filtros de quem
 * trabalha iam para o histórico de páginas (A19). Vai só o caminho, e mudar só a query (paginar,
 * filtrar) não conta como página nova.
 */
export const useMonitoramentoNavigation = (options: UseMonitoramentoNavigationOptions) => {
    const {
        fonteCodigo,
        basePath,
        enabled = true,
        onError,
    } = options;

    const lastPathRef = useRef<string>(caminhoSemQuery(window.location.href));
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const handleError = useCallback((error: unknown) => {
        console.warn("[Monitoramento] Erro ao enviar actualização de página:", error);
        if (onError) onError(error as Error);
    }, [onError]);

    useEffect(() => {
        if (!enabled) return;

        const sendPageUpdate = (caminho: string, titulo: string) => {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

            debounceTimerRef.current = setTimeout(async () => {
                try {
                    await req().post(`${basePath}/monitoramento/presenca/heartbeat`, {
                        fonte_codigo: fonteCodigo,
                        socket_id: socketIdDaWeb(fonteCodigo),
                        pagina_actual: caminho,
                        pagina_titulo: titulo,
                    });
                } catch (error) {
                    handleError(error);
                }
            }, 500);
        };

        const removeListener = router.on("navigate", (event) => {
            const caminho = caminhoSemQuery(event.detail.page.url);

            if (caminho !== lastPathRef.current) {
                lastPathRef.current = caminho;
                sendPageUpdate(caminho, document.title);
            }
        });

        return () => {
            removeListener();
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, [enabled, fonteCodigo, basePath, handleError]);

    return null;
};

export default useMonitoramentoNavigation;
